import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useState, useRef, useEffect, type ReactElement } from 'react'

interface Tool {
  name: string
  path: string
  icon: ReactElement
}

// Linear-style icons (thin strokes, minimal)
const tools: Tool[] = [
  {
    name: 'Test Case Generator',
    path: '/test-case/',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25">
        <rect x="3" y="2" width="10" height="12" rx="1.5" />
        <path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    name: 'Offers Audit',
    path: '/offers-audit/',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25">
        <circle cx="8" cy="8" r="5.5" />
        <circle cx="8" cy="8" r="2" />
        <path d="M8 2.5v1M8 12.5v1M2.5 8h1M12.5 8h1" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    name: 'Risk Prioritization',
    path: '/risk-prioritization/',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25">
        <path d="M3 12h2v2H3zM7 8h2v6H7zM11 4h2v10h-2z" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    name: 'Test Case Updater',
    path: '/test-case-updater/',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25">
        <rect x="3" y="2" width="10" height="12" rx="1.5" />
        <path d="M5.5 5.5h5M5.5 8h3" strokeLinecap="round" />
        <path d="M10.5 9l-3 3v1h1l3-3z" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    name: 'RT Audit',
    path: '/rt-audit/',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25">
        <rect x="2" y="3" width="12" height="10" rx="1.5" />
        <path d="M2 6h12M5 6v7" strokeLinecap="round" />
        <path d="M7.5 8.5l1.5 1.5 3-3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
]

export default function Sidebar() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const profileMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false)
      }
    }
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setProfileMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeydown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeydown)
    }
  }, [])

  const isToolActive = (toolPath: string): boolean => {
    return location.pathname.startsWith(toolPath)
  }

  const handleToolClick = (path: string) => {
    navigate(path)
  }

  const getUserInitials = (): string => {
    if (!user) return '?'
    if (user.givenName && user.familyName) {
      return user.givenName.charAt(0).toUpperCase() + user.familyName.charAt(0).toUpperCase()
    } else if (user.name) {
      const nameParts = user.name.split(' ')
      if (nameParts.length >= 2) {
        return nameParts[0].charAt(0).toUpperCase() + nameParts[nameParts.length - 1].charAt(0).toUpperCase()
      }
      return user.name.charAt(0).toUpperCase()
    }
    return user.email.charAt(0).toUpperCase()
  }

  return (
    <aside className="fixed left-0 top-0 h-screen w-[220px] bg-linear-bg-sidebar border-r border-linear-border flex flex-col">
      {/* Workspace Header - Linear style */}
      <div className="h-[52px] flex items-center px-4 border-b border-linear-border">
        <Link to="/" className="flex items-center gap-2.5 group">
          {/* Workspace icon with mustard background */}
          <div className="w-[18px] h-[18px] bg-mustard rounded flex items-center justify-center flex-shrink-0">
            <span className="text-[10px] font-semibold text-white">FD</span>
          </div>
          <span className="text-[13px] font-medium text-linear-text group-hover:text-linear-accent transition-colors">
            Five & Done QA
          </span>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3">
        {/* Main Navigation Section */}
        <div className="px-3">
          {/* Section Label */}
          <div className="flex items-center h-[26px] px-[7px]">
            <span className="text-[11px] font-medium text-linear-text-muted uppercase tracking-wide">
              Tools
            </span>
          </div>

          {/* Navigation Items */}
          <div className="space-y-[2px]">
            {tools.map((tool) => {
              const isActive = isToolActive(tool.path)
              return (
                <button
                  key={tool.path}
                  onClick={() => handleToolClick(tool.path)}
                  className={`
                    w-full flex items-center gap-2
                    pl-[7px] pr-[8px] py-[5.5px]
                    rounded
                    text-[13px] font-medium
                    transition-colors duration-75
                    ${isActive
                      ? 'bg-linear-text-muted/20 text-linear-text'
                      : 'text-linear-text-secondary hover:bg-linear-text-muted/10 hover:text-linear-text'
                    }
                  `}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <span className="text-linear-text-muted flex-shrink-0">{tool.icon}</span>
                  <span className="truncate">{tool.name}</span>
                </button>
              )
            })}
          </div>
        </div>
      </nav>

      {/* User Profile Section - Bottom */}
      {user && (
        <div className="border-t border-linear-border p-3" ref={profileMenuRef}>
          <div className="relative">
            <button
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              className="w-full flex items-center gap-2 pl-[7px] pr-[8px] py-[5.5px] rounded hover:bg-linear-text-muted/10 transition-colors duration-75"
              aria-expanded={profileMenuOpen}
              aria-haspopup="true"
            >
              {/* User Avatar */}
              <div className="w-[22px] h-[22px] bg-linear-accent rounded flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-medium text-white">{getUserInitials()}</span>
              </div>
              <span className="text-[13px] font-medium text-linear-text-secondary truncate flex-1 text-left">
                {user.name || user.email.split('@')[0]}
              </span>
              <svg
                className={`w-3.5 h-3.5 text-linear-text-muted transition-transform duration-150 flex-shrink-0 ${profileMenuOpen ? 'rotate-180' : ''}`}
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* Profile Dropdown */}
            {profileMenuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-1 bg-linear-bg-card border border-linear-border-input rounded-lg shadow-lg overflow-hidden animate-scale-in">
                {/* User Info */}
                <div className="px-3 py-2.5 border-b border-linear-border">
                  <p className="text-[13px] font-medium text-linear-text truncate">{user.name || user.email}</p>
                  <p className="text-[11px] text-linear-text-muted truncate mt-0.5">{user.email}</p>
                </div>

                {/* Menu Items */}
                <div className="py-1">
                  <button
                    onClick={() => {
                      navigate('/settings')
                      setProfileMenuOpen(false)
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-linear-text hover:bg-linear-text-muted/10 transition-colors"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25">
                      <circle cx="8" cy="8" r="2" />
                      <path d="M8 3v1M8 12v1M3 8h1M12 8h1M4.5 4.5l.7.7M10.8 10.8l.7.7M4.5 11.5l.7-.7M10.8 5.2l.7-.7" strokeLinecap="round" />
                    </svg>
                    <span>Settings</span>
                  </button>
                  <button
                    onClick={() => {
                      logout()
                      setProfileMenuOpen(false)
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-error hover:bg-error/10 transition-colors"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25">
                      <path d="M6 2H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2M10.5 11.5L14 8l-3.5-3.5M6 8h8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>Sign out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  )
}
